import React, { useState, useRef, useEffect } from 'react';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import UtilityTrendGraph from './UtilityTrendGraph';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Box from '@mui/material/Box'
import OutlinedInput from '@mui/material/OutlinedInput';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import useAxiosProtect from "../../../../hooks/useAxiosProtect";
import useAuth from '../../../../hooks/useAuth';
import UtilityPieChart from './UtilityPieChart';
import { isMobile } from "react-device-detect";

const UtilityTrendContainer = props => {    
    const [activeParameter, setActiveParameter] = useState('water');

    const [year, setYear] = useState(new Date().getFullYear());
    const [yearType, setYearType] = useState('calendar');

    const [siteId, setSiteId] = useState(null);
    const [sites, setSites] = useState([]);
    const [siteNames, setSiteNames] = useState([]);

    const [allSelected, setAllSelected] = useState(false);

    const [siteResults, setSiteResults] = useState({});
    const [combinedData, setCombinedData] = useState([]);
    const [costTrends, setCostTrends] = useState([]);
    const [emissionTrends, setEmissionTrends] = useState([]);

    const [barType, setBarType] = useState('total');

    const [isLoading, setIsLoading] = useState(true);

    var init = useRef(true);

    const axiosProtect = useAxiosProtect();
    const {auth, _} = useAuth();

    const [baselinePcts, setBaselinePcts] = useState({
        electricity: 0,
        gas: 0
    });

    const [totalCosts, setTotalCosts] = useState(null);
    const [totalEmissions, setTotalEmissions] = useState(null);

    const fetchBaselineGraph = async (namesOverride) => {
        const names = namesOverride ?? siteNames;

        if (!sites.length || !names.length) return;

        const siteIds = sites
            .filter(s => names.includes(s.name))
            .map(s => s.id);

        setIsLoading(true);

        try{
            const response = await axiosProtect.get(
                `/u/utilities/baseline-graph?siteIds=${siteIds.join(',')}&year=${year}&yearType=${yearType}`
            );

            setSiteResults(response.data.result);
            setCombinedData(response.data.combinedResult);

            setCostTrends(response.data.costTrends || []);
            setEmissionTrends(response.data.emissionTrends || []);

            setBaselinePcts({
                electricity: response.data.electricityBaseline,
                gas: response.data.gasBaseline
            });

            setTotalCosts(response.data.totalCosts);
            setTotalEmissions(response.data.totalEmissions);
        }finally{
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if(!init.current) return;

        axiosProtect.get(`/auth/query/list-client-site-access?uid=${auth.id}`).then(response => {
            // let siteId = window.location.href.split('/').slice(-1)[0];
            let siteId = props.clientView && response.data.sites.length > 0 ? response.data.sites[0].id : window.location.href.split('/').slice(-1)[0];
            setSiteId(siteId);

            let activeOrgID = '';
            let activeSiteName = '';
            for(let i = 0;i<response.data.sites.length;++i){
                if(response.data.sites[i].id == siteId){
                    activeOrgID = response.data.sites[i].org_id;
                    activeSiteName = response.data.sites[i].name;
                    break;
                }
            }

            const filteredSites = [...response.data.sites.sort((a, b) => a.name.localeCompare(b.name))].filter(site => site.org_id === activeOrgID);

            setSiteNames([activeSiteName])

            setSites(filteredSites);
        }).catch(error => {

        });

        init.current = false;
    }, []);

    useEffect(() => {
        if(!sites.length || !siteNames.length) return;
        fetchBaselineGraph();
    }, [year, yearType, siteNames, sites]);

    const SELECT_ALL = 'select-all-trigger';

    const prevSiteNamesRef = useRef(siteNames);

    useEffect(() => {
        prevSiteNamesRef.current = siteNames;
    }, [siteNames]);

    const handleSiteSelection = e => {
        if(isLoading) return;

        const raw = e.target.value;
        const nextRaw = Array.isArray(raw) ? raw : raw.split(',');

        const allNames = sites.map(s => s.name);
        const firstName = allNames[0] || null;

        const userClickedSelectAllRow = nextRaw.includes(SELECT_ALL);

        let nextNames = [];

        if(userClickedSelectAllRow){
            if(!allSelected){
                nextNames = allNames;
                setAllSelected(true);
            } else {
                nextNames = firstName ? [firstName] : [];
                setAllSelected(false);
            }
        } else {
            const cleaned = nextRaw.filter(v => v !== SELECT_ALL);

            nextNames = cleaned.length ? cleaned : (firstName ? [firstName] : []);

            setAllSelected(nextNames.length === allNames.length);
        }

        const siteIds = sites.filter(s => nextNames.includes(s.name)).map(s => s.id);

        setSiteNames(nextNames);

        setIsLoading(true);
    }

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;

    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            },
        },
    };

    return(
        <div>
            <div style={{width:'100%', height: props.isMobile ? 'fit-content' : '70vh', display:'flex', flexDirection: props.isMobile ? 'column' : 'row', padding:'1%'}}>
                <div style={{flex:'2', minWidth:'0'}}>
                    <FormControl fullWidth sx={{marginBottom:1}}>
                        <InputLabel id="demo-multiple-checkbox-label">Sites</InputLabel>
                        <Select
                            labelId="demo-multiple-checkbox-label"
                            id="demo-multiple-checkbox"
                            multiple
                            value={siteNames}
                            onChange={handleSiteSelection}
                            input={<OutlinedInput label="Sites" />}
                            MenuProps={MenuProps}
                            disabled={isLoading}
                            renderValue={(selected) => (
                                <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {selected.filter(v => v !== 'select-all-trigger').join(', ')}
                                </Box>
                            )}
                        >
                            {
                                sites.length > 0 &&
                                <MenuItem key={'select-all'} value={'select-all-trigger'} disabled={isLoading}>
                                    <Checkbox checked={allSelected} />
                                    <ListItemText primary={'-- SELECT ALL --'} />
                                </MenuItem>
                            }
                            {sites.map(site => (
                                <MenuItem key={site.id} value={site.name} disabled={isLoading}>
                                    <Checkbox checked={siteNames.includes(site.name)} />
                                    <ListItemText primary={site.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="dense">
                        <InputLabel id="gas-create-init-type">Parameter</InputLabel>
                        <Select
                            value={activeParameter}
                            label="Parameter"
                            onChange={e => setActiveParameter(e.target.value)}
                            disabled={isLoading}
                        >
                            <MenuItem value='water'>Water</MenuItem>
                            <MenuItem value='electricity'>Electricity</MenuItem>
                            <MenuItem value='gas'>Gas</MenuItem>
                            <MenuItem value='ghg'>GHG</MenuItem>
                            <MenuItem value='cost'>Cost</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="dense">
                        <InputLabel id="gas-create-init-type">Year</InputLabel>
                        <Select
                            value={year}
                            label="Year"
                            disabled={isLoading}
                            onChange={e => {
                                setIsLoading(true);
                                setYear(e.target.value);
                            }}
                        >
                            {
                                Array.from({length: new Date().getFullYear() - 2018 + 1}, (_, i) => 2018 + i).map((yr, _) => (
                                    <MenuItem value={yr}>{yr}</MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>

                    <br/>
                    <br/>

                    <FormControl disabled={isLoading} style={props.isMobile ? {width:'100%',textAlign:'center'} : {}}>
                        <FormLabel id="demo-row-radio-buttons-group-label">Year type</FormLabel>
                        <RadioGroup
                            row
                            style={props.isMobile ? {margin:'auto'} : {}}
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                        >
                            <FormControlLabel value="calendar" control={<Radio checked={yearType === 'calendar'} onClick={() => {
                                setIsLoading(true);
                                setYearType('calendar');
                            }}/>} label="Calendar" />
                            <FormControlLabel value="fiscal" control={<Radio checked={yearType === 'fiscal'} onClick={() => {
                                setIsLoading(true);
                                setYearType('fiscal');
                            }}/>} label="Fiscal" />
                        </RadioGroup>
                    </FormControl>

                    <br/>
                    <br/>

                    <FormControl disabled={isLoading} style={props.isMobile ? {width:'100%',textAlign:'center'} : {}}>
                        <FormLabel id="demo-row-radio-buttons-group-label">View by</FormLabel>
                        <RadioGroup
                            row
                            style={props.isMobile ? {margin:'auto'} : {}}
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                        >
                            <FormControlLabel value="total" control={<Radio checked={barType === 'total'} onClick={() => {
                                setBarType('total');
                            }}/>} label="Total" />
                            <FormControlLabel value="site" control={<Radio checked={barType === 'site'} onClick={() => {
                                setBarType('site');
                            }}/>} label="Site" />
                        </RadioGroup>
                    </FormControl>

                    {
                        props.isMobile && 
                        <>
                        <br/>
                        <br/>
                        </>
                    }
                </div>

                <div style={{width:'1vw'}}></div>

                <div style={props.isMobile ? {height:'50vh'} : {flex:'10'}}>
                    <UtilityTrendGraph 
                        siteResults={siteResults}
                        combinedData={combinedData}
                        selectedSiteNames={siteNames}
                        costTrends={costTrends}
                        emissionTrends={emissionTrends}
                        activeParameter={activeParameter}
                        baselinePcts={baselinePcts}
                        isLoading={isLoading}
                        barType={barType} 
                        isMobile={props.isMobile}
                    />
                </div>

                <div style={{width:'1vw'}}></div>
            </div>

            <br/>

            <div style={{width:'100%', height:props.isMobile ? "fit-content" : '25vw', padding:'1vw', display:'flex', flexDirection: props.isMobile ? 'column' : 'row'}}>
                <div style={{flex:'1'}}></div>

                <div style={{width: props.isMobile ? '100%' : '25vw', height:'100%', textAlign:'center'}}>
                    <p>Expenditures</p>
                    <UtilityPieChart totalObject={totalCosts}/>
                </div>

                <div style={{flex:'1'}}></div>

                {props.isMobile && <br/>}

                <div style={{width: props.isMobile ? '100%' : '25vw', height:'100%', textAlign:'center'}}>
                    <p>GHG Emissions</p>
                    <UtilityPieChart totalObject={totalEmissions}/>
                </div>

                <div style={{flex:'1'}}></div>
            </div>
            <br/>
        </div>
    );
}

export default UtilityTrendContainer;